import { Component, OnInit, EventEmitter } from '@angular/core';
import {
  AlertService,
  ContractService,
  ModalService,
  CompanyService,
  StorageKey,
  StorageService, ContractGlobalService, UserGroupService
} from 'src/app/core/services';
import { AlertItem, AlertType, ComponentModal, Contract, User } from 'src/app/core/models';
import { ContractCreateOrUpdateComponent } from './contract-create-or-update/contract-create-or-update.component';
import { ContractViewModelComponent } from './contract-view-model/contract-view-model.component';
import { ClosedModalError } from 'src/app/core/errors';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment-timezone';
import { updateCurrentContracts } from '../../../core/utils/updateLocalStorage';
import { VerifyPermissionUrl } from '../utils/verify-permission-url';
import { Gb } from 'src/app/core/services/growthbook.service';

// eslint-disable-next-line no-shadow
enum Module {
  preProcess = 'preProcess',
  violations = 'violations',
  traffic = 'traffic',
  status = 'status',
  monitoring = 'monitoring',
  backup = 'backup',
  surveillance = 'surveillance'
}
@Component({
  selector: 'app-manage-contract',
  templateUrl: './manage-contract.component.html',
  styleUrls: ['./manage-contract.component.sass']
})
export class ManageContractComponent implements OnInit {
  public contracts: Array<Contract> = [];
  public contract;
  public modalState;
  public loading: boolean;
  public companies: Array<any>;
  public modelChanged: Subject<string> = new Subject<string>();
  public searchForm: FormGroup;
  public params: any = {};
  public deleteEvent: EventEmitter<any> = new EventEmitter();
  public module = Object.values(Module);
  public moduleI18n = [];
  public user: User = new User();
  public contractGlobal = null;
  public gb: Gb;
  public expiredFirstSort = (a, b) => (a.status === 'expired' ? 0 : 1) - (b.status === 'expired' ? 0 : 1);

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(
    private modalService: ModalService,
    public contractService: ContractService,
    private companyService: CompanyService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private storageService: StorageService,
    private contractGlobalService: ContractGlobalService,
    private userGroupService: UserGroupService,
    private verifyPermissionUrl: VerifyPermissionUrl
  ) {
    this.modelChanged.pipe(
      debounceTime(500))
      .subscribe(() => {
        this.handleSearch();
      });
    if (this.contractGlobalService.contracts != null) {
      this.contractGlobal = this.contractGlobalService.contracts.length === 1 ? this.contractGlobalService.contracts[0] : null;
    }
    this.gb = new Gb();
  }

  async ngOnInit() {
    this.verifyPermissionUrl.execute(['ViewContract']);
    this.loading = true;
    await this.gb.init();
    const user = this.storageService.get(StorageKey.currentUser);
    if (user != null) {
      this.user = user;
    }
    const filterObj = {};
    if (this.contractGlobal) {
      filterObj['code[in]'] = `[${this.contractGlobal}]`;
      this.params = filterObj;
      delete this.params.id;
    }
    this.getCompanies();
    this.createSearchForm();
    this.changeContracts();
    document.addEventListener('contractSaved', event => {
      // @ts-ignore
      this.contract = event.detail;
    });
  }

  changeContracts() {
    this.contractGlobalService.contractEvent.subscribe(async res => {
      this.contractGlobal = res;
      delete this.params['code[in]'];
      if (this.contractGlobal) {
        this.params['code[in]'] = `[${this.contractGlobal}]`;
      }
    });
  }

  async getCompanies() {
    await this.companyService.getAll(null, true).then(res => {
      this.companies = res;
      this.loading = false;
    });
  }

  createSearchForm() {
    this.searchForm = this.formBuilder.group({
      search: [''],
      isEnabled: [''],
      companyId: [''],
      startsAt: [''],
      endsAt: [''],
      status: ['']
    });
  }

  isNumber(value) {
    return !isNaN(Number(value));
  }

  async output(name, callback) {
    return await document.addEventListener(name, callback);
  }

  openCreateOrUpdateContract(contract = {}) {
    setTimeout(() => {
      if (contract) {
        this.userGroupService.getAll(null, true).then(res => {
          const usergroup = res;
          if (usergroup.length) {
            updateCurrentContracts(usergroup[0].contractId, this.storageService, usergroup);
          }
        });
      }
    }, 3000);
    this.modalService.show(new ComponentModal(ContractCreateOrUpdateComponent, contract))
      .then(data => {
        this.loading = true;
        setTimeout(() => {
          if (this.user.superUser) {
            window.location.reload();
          } else {
            const savedContract = this.contract;
            const i = this.contracts.findIndex(e => e.id === savedContract?.id);
            if (i === -1) {
              this.contracts.push(this.contract);
              this.loading = false;
              return savedContract;
            } else {
              window.location.reload();
            }
          }
          this.loading = false;
        }, 3000);
      }).catch(err => {
        if (err instanceof ClosedModalError) {
          const modalError = err as ClosedModalError;
          this.modalState = (modalError.modal as ComponentModal).component.instance.lastState;
        } else {
          this.modalState = null;
        }
      });
  }

  setContracts(list: Contract[]) {
    this.loading = false;
    this.contracts = [];

    list.forEach((contract: Contract) => {
      const isDuplicate = this.contracts.some(c => c.id === contract.id);

      if (!isDuplicate) {
        if (typeof contract.companies === 'string') {
          contract.companies = JSON.parse(contract.companies);
        }
        this.contracts.push(contract);
      }
    });

    this.contracts.sort((a, b) => {
      if (a.status === 'expired' && b.status !== 'expired') {
        return -1;
      }
      if (a.status !== 'expired' && b.status === 'expired') {
        return 1;
      }
      return 0;
    });
  }

  handleSearch() {
    this.loading = true;
    this.contracts = [];
    const filterObj = { status: null, name: null, code: null };
    const search = this.searchForm.get('search').value;
    if (search !== '') {
      const value = search.replaceAll('.', '').replaceAll('/', '').replaceAll('-', '');
      if (this.isNumber(value)) {
        filterObj['code[like]'] = `%${value}%`;
        delete filterObj.name;
      } else {
        filterObj['name[like]'] = `%${value}%`;
        delete filterObj.code;
      }
    }
    const companyId = this.searchForm.get('companyId').value;
    const status = this.searchForm.get('status').value;
    const start = this.searchForm.get('startsAt').value;
    const end = this.searchForm.get('endsAt').value;
    let startsAt = null;
    let endsAt = null;
    if (start != null && start !== '') {
      startsAt = moment(start).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .format('YYYY-MM-DD[T]HH:mm:ss');
    }
    if (end != null && end !== '') {
      endsAt = moment(end).set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .format('YYYY-MM-DD[T]HH:mm:ss');
    }
    if (startsAt) {
      filterObj['startsAt[gte]'] = moment(startsAt).format();
    }
    if (endsAt) {
      filterObj['endsAt[lte]'] = moment(endsAt).format();
    }

    if (companyId != null && companyId !== '' && companyId.length > 0) {
      filterObj['companyIds[in]'] = `[${companyId}]`;
    }
    if (status != null && status !== '' && status !== 'all') {
      filterObj.status = `${status}`;
    } else if (status === 'all' || !status) {
      delete filterObj.status;
    }
    if (filterObj.name === null) {
      delete filterObj.name;
    }
    if (filterObj.code === null) {
      delete filterObj.code;
    }
    this.params = filterObj;
  }

  searchKey(text: string) {
    this.modelChanged.next(text);
  }

  contractStatus(status) {
    return status === 'enabled' ? true : false;
  }

  setModuleI18n(moduleResult) {
    this.moduleI18n = moduleResult;
  }

  getModuleI18n(item) {
    const moduleFilter = this.moduleI18n.filter(module => module.id === item);
    return moduleFilter[0].value;
  }

  updateContractEdit(contract) {
    return this.contractService.update(Contract.create(contract))
      .then(res => {
        this.contract = res;
        this.contract.companies = this.contract.Companies || this.contract.companies;
        this.alertService.show(new AlertItem('ContractSaved', AlertType.success));
        return res;
      }).catch(error => {
        this.alertService.show(new AlertItem('ContractSaveError', AlertType.danger));
      });
  }

  openContractView(contract = {}) {
    const data = {
      contract,
      modules: this.moduleI18n
    };
    this.modalService.show(new ComponentModal(ContractViewModelComponent, data))
      .catch(err => {
        if (err instanceof ClosedModalError) {
          const modalError = err as ClosedModalError;
        }
      });
  }

  getCompanyName(id) {
    const company = this.companies?.filter(c => c.id === id);
    return (company?.length > 0 && company[0].name) || '';
  }
}
